import React from "react";
import { Navigate, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LogOut, Car, Mail, LayoutGrid } from "lucide-react";

const LOGO_URL = "/elysian-logo.png";

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  if (loading) {
    return <div className="min-h-screen bg-[#0B0B0B]" />;
  }
  if (!user) return <Navigate to="/admin/login" replace />;

  const onLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const navItems = [
    { to: "/admin", label: "Overview", icon: <LayoutGrid size={15} />, end: true },
    { to: "/admin/vehicles", label: "Vehicles", icon: <Car size={15} /> },
    { to: "/admin/inquiries", label: "Inquiries", icon: <Mail size={15} /> },
  ];

  return (
    <div className="min-h-screen bg-[#0B0B0B] text-white" data-testid="admin-layout">
      <div className="flex">
        <aside className="w-64 hidden md:flex flex-col border-r border-white/5 bg-[#0F0F0F] min-h-screen">
          <div className="p-6 border-b border-white/5">
            <img src={LOGO_URL} alt="Elysian Cars" className="h-8 w-auto object-contain" />
          </div>
          <nav className="p-4 flex-1 flex flex-col gap-1">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`admin-nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 text-xs tracking-[0.22em] uppercase transition-colors ${
                    isActive
                      ? "bg-[#C6A15B]/10 text-[#C6A15B] border-l-2 border-[#C6A15B]"
                      : "text-zinc-400 hover:text-white hover:bg-white/[0.03]"
                  }`
                }
              >
                {n.icon}
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="p-4 border-t border-white/5 flex items-center justify-between">
            <div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">Signed in</div>
              <div className="text-sm text-white truncate max-w-[160px]">{user.email}</div>
            </div>
            <button
              onClick={onLogout}
              data-testid="admin-logout"
              className="p-2 text-zinc-400 hover:text-[#C6A15B] border border-white/10 hover:border-[#C6A15B]/40"
              aria-label="Logout"
            >
              <LogOut size={14} />
            </button>
          </div>
        </aside>

        <main className="flex-1 min-h-screen">
          <div className="md:hidden flex items-center justify-between p-4 border-b border-white/5 bg-[#0F0F0F]">
            <img src={LOGO_URL} alt="Elysian Cars" className="h-7 w-auto object-contain" />
            <div className="flex gap-2">
              {navItems.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  className={({ isActive }) =>
                    `p-2 border ${isActive ? "border-[#C6A15B] text-[#C6A15B]" : "border-white/10 text-zinc-400"}`
                  }
                >
                  {n.icon}
                </NavLink>
              ))}
              <button onClick={onLogout} className="p-2 border border-white/10 text-zinc-400">
                <LogOut size={14} />
              </button>
            </div>
          </div>
          <div className="p-6 md:p-10">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
